import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useHistory } from "react-router-dom";

import {
    Box, Typography, Card, IconButton, MenuItem, Grid, Divider
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getImage } from "helpers/utils";

import ClearSharpIcon from "@mui/icons-material/ClearSharp";
import AddBoxSharpIcon from "@mui/icons-material/AddBoxSharp";
import IndeterminateCheckBoxSharpIcon from "@mui/icons-material/IndeterminateCheckBoxSharp";
import ButtonComponent from "components/Button";
import MockProductList from "components/elements/main/MockProductList";
import { H5 } from "components/CustomTypography";

import * as Actions from "actions";
import * as ActionTypes from "constants/ActionTypes";

import { getCartTotal } from "services";
import { CURRENCY } from "config/constants";
import SelectFieldComponent from "components/SelectField";
import { Notification } from "components/Atoms";
import EmptyShoppingCart from "components/elements/main/EmptyShoppingCart";

// Import checkout flow components
import shopCart from "../../checkout/shopCart";
import shopCheckOut from "../../checkout/shopCheckout";
import shopPaymentCheckout from "../../checkout/shopCheckout/payment-page";
import shopOrderSuccess from "../../checkout/shopCheckout/success-page";
import UnifiedSwipeTrigger from "../../../../elements/main/UnifiedSwipeTrigger";

const ShopCartTabContent = (props) => {
    const {
        serviceError, reqStatus, isLoading,
        cartItems, symbol, total, getCartProducts, bootupInfo, removeProductFromCart,
        incrementProductInCart, decrementProductInCart, updateProductSizeInCart, shopId, location,
    } = props;

    // Determine current checkout sub-page based on URL
    const getCurrentCheckoutPage = () => {
        const path = location.pathname;
        if (path.includes('/checkout')) {
            return { type: 'checkout' };
        } else if (path.includes('/payment-page')) {
            return { type: 'payment' };
        } else if (path.includes('/order-success')) {
            return { type: 'order-success' };
        }
        return { type: 'cart' };
    };

    const currentCheckoutPage = getCurrentCheckoutPage();

    const handleBackToCart = () => {
        window.history.back();
    };

    const renderSubPageHeader = (title) => (
        <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 3,
            pb: 2,
            borderBottom: '1px solid #e0e0e0'
        }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {title}
            </Typography>
        </Box>
    );

    useEffect(() => {
        getCartProducts(bootupInfo.appState.subdomain_shopId, bootupInfo.appState.isWebsite);
    }, []);

    const sendNotification = (type, { title, description = "" }) => {
        Notification[type]({
            message: title,
            description,
        });
    };

    useEffect(() => {
        if (serviceError && reqStatus) {
            const feedback = {
                title: serviceError,
                description: serviceError,
            };
            sendNotification("error", feedback);
        }
        switch (reqStatus) {
            case ActionTypes.USER_UPDATE_PRODUCT_SIZE_IN_CART_SUCCESS:
                getCartProducts(bootupInfo.appState.subdomain_shopId, bootupInfo.appState.isWebsite);
                break;
            default:
                break;
        }
    }, [serviceError, reqStatus]);

    const getPrice = (symbol, item) => {
        const price = item.seller_price
            - (item.product && item.product.discount
                ? (item.seller_price * (parseInt(item.product.discount, 10) / 100)) : 0);
        return (
            <Typography variant="caption" gutterBottom>
                {symbol}
                {price}
                {item.product.discount && (
                    <del>
                        <span className="money">
                            {symbol}
                            {item.seller_price}
                        </span>
                    </del>
                )}
            </Typography>
        );
    };

    const handleSizeClick = (event, item) => {
        updateProductSizeInCart(item, props.bootupInfo.appState.subdomain_shopId, props.bootupInfo.appState.isWebsite, event.target.value);
    };

    const validateSizeInCart = () => {
        let valid = true;
        cartItems.map(function (item) {
            if (item.size && item.size.length > 0) {
                if (!(item.size.includes(item.product_size))) {
                    valid = false;
                }
            }
        });
        return valid;
    };

    const getSize = (item) => {
        const availablesize = item.size;

        if (!(availablesize.length > 0)) {
            return (<></>);
        }

        const inputSizeOptions = availablesize.map((item) => {
            return (
                <MenuItem value={item}>{item}</MenuItem>
            );
        });

        return (
            <Typography variant="caption" gutterBottom>
                {item.size && (
                    <SelectFieldComponent
                        options={inputSizeOptions}
                        label="Size"
                        value={item.product_size}
                        onChange={(event) => handleSizeClick(event, item)}
                    />
                )}
            </Typography>
        );
    };

    const getquantity = (cartItem) => (
        <Box display="flex">
            <IconButton color="primary" size="small" onClick={cartItem.product_quantity > 1 && !isLoading ? () => decrementProductInCart(cartItem, props.bootupInfo.appState.subdomain_shopId, props.bootupInfo.appState.isWebsite) : ""}>
                <IndeterminateCheckBoxSharpIcon />
            </IconButton>
            <Typography className="p-1">{cartItem.product_quantity}</Typography>
            <IconButton color="primary" size="small" onClick={!isLoading ? () => incrementProductInCart(cartItem, props.bootupInfo.appState.subdomain_shopId, props.bootupInfo.appState.isWebsite) : ""}>
                <AddBoxSharpIcon />
            </IconButton>
        </Box>
    );

    const history = useHistory();

    const handleCheckoutPage = (page) => (e) => {
        if (validateSizeInCart()) {
            history.push(page);
        } else {
            sendNotification("error", { title: "Size", description: "Select a proper size" });
        }
    };

    const handleBackHistory = () => {
        history.goBack();
    };

    let checkOutLink;
    if (bootupInfo.appState.isWebsite) {
        checkOutLink = "/cart/checkout";
    } else {
        checkOutLink = `/shop/${shopId}/cart/checkout`;
    }

    const renderCheckoutFlow = () => {
        switch (currentCheckoutPage.type) {
            case 'checkout':
                return (
                    <Box>
                        {renderSubPageHeader('Checkout')}
                        {React.createElement(shopCheckOut, props)}
                    </Box>
                );
            case 'payment':
                return (
                    <Box>
                        {renderSubPageHeader('Payment')}
                        {React.createElement(shopPaymentCheckout, props)}
                    </Box>
                );
            case 'order-success':
                return (
                    <Box>
                        {renderSubPageHeader('Order Confirmation')}
                        {React.createElement(shopOrderSuccess, props)}
                    </Box>
                );
            default:
                return (
                    <Box>
                        {renderSubPageHeader('Cart')}
                        {React.createElement(shopCart, props)}
                    </Box>
                );
        }
    };

    return (
        <>
            { renderCheckoutFlow() }
            <UnifiedSwipeTrigger onLeftSwipeComplete={() => { handleBackHistory() }} />
        </>
    );

};

ShopCartTabContent.defaultProps = {
    symbol: CURRENCY,
};

ShopCartTabContent.propTypes = {
    bootupInfo: PropTypes.objectOf(PropTypes.any).isRequired,
    cartItems: PropTypes.arrayOf(PropTypes.object).isRequired,
    removeProductFromCart: PropTypes.func.isRequired,
    incrementProductInCart: PropTypes.func.isRequired,
    decrementProductInCart: PropTypes.func.isRequired,
    getCartProducts: PropTypes.func.isRequired,
    total: PropTypes.number.isRequired,
    symbol: PropTypes.string.isRequired,
    shopId: PropTypes.string.isRequired,
    location: PropTypes.object.isRequired,
};

const _ShopCartTabContent = connect(
    ({ cartList, bootupInfo }) => ({
        bootupInfo,
        symbol: bootupInfo.symbol,
        reqStatus: cartList.reqStatus,
        isLoading: cartList.isLoading,
        serviceError: cartList.serviceError,
        cartItems: cartList.cart,
        total: getCartTotal(cartList.cart),
    }),
    (dispatch) => bindActionCreators({ ...Actions }, dispatch),
)(ShopCartTabContent);

export { _ShopCartTabContent as default };


